/**
 * `SCR-RETENTION` — P-RETAIN-01 at the screen. OWNER: web-frontend-engineer.
 *
 * The house has always DECLARED how long it keeps things — two years for access
 * logs, seven for commercial records, the life of the relationship for a body.
 * P-RETAIN-01 built the thing that reads those declarations against the actual
 * database and says what is past its date. This is where a person reads it.
 *
 * ── IT REPORTS. IT DELETES NOTHING, AND THE PAGE SAYS SO ──────────────────
 *
 * That sentence is the server's and it is rendered first, before the table,
 * because it is the part people assume the other way round. A page called
 * "what is kept" that quietly ran a sweep would be the worst possible thing to
 * leave armed overnight; a page that reports and says it reports is a page
 * somebody can act on with their eyes open.
 *
 * ── THREE KINDS OF ANSWER, NOT ONE ────────────────────────────────────────
 *
 * A retention window is a number of months for a log and a lie for a body. The
 * report distinguishes them and so does this table:
 *   · a WINDOW, in months, with a count of what is past it;
 *   · KEPT FOR LIFE — as long as he is a client, which is a real answer and not
 *     an absent one;
 *   · CRYPTO-SHRED — erasure destroys the key, not the row, and the rows survive
 *     as unreadable ciphertext. Showing "0 overdue" against that would be
 *     describing a deletion that by design never happens.
 *
 * And the last block is the one that keeps the whole thing honest: a policy
 * pointing at a table nothing counts is a rule nobody can check, so it is named
 * rather than left out of the total.
 */

import { t, tDynamic } from '@pt/copy';
import { dateInWords } from '@pt/view';
import { api } from '../../lib/api.ts';
import { useResource } from '../../lib/resource.ts';
import type { RetentionLine } from '../../lib/contracts.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { BackOfficeTable, type Column } from '../../components/backoffice.tsx';
import { FactLine, Statement } from '../../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../../components/states.tsx';

export function RetentionScreen() {
  const report = useResource(() => api.retention(), []);
  const data = report.data;

  const columns: readonly Column<RetentionLine>[] = [
    { id: 'subject', headingKey: 'retain.subject', render: (row) => row.subject },
    { id: 'basis', headingKey: 'retain.basis', render: (row) => row.basis },
    {
      id: 'window',
      headingKey: 'retain.window',
      /* A window in months, or the honest words for "as long as he is a
         client". Never a number standing in for a relationship. */
      render: (row) =>
        row.retainMonths === null
          ? t('retain.forLife')
          : row.retainMonths === 1
            ? t('retain.months.one')
            : t('retain.months', { monthCount: String(row.retainMonths) }),
    },
    {
      id: 'rows',
      headingKey: 'retain.rows',
      numeric: true,
      render: (row) => String(row.rows),
    },
    {
      id: 'overdue',
      headingKey: 'retain.overdue',
      numeric: true,
      /* `null` is not zero. A subject erased by destroying a key has no
         overdue count, and printing 0 would describe a deletion that never
         happens. Empty says "this column does not apply here". */
      render: (row) => (row.overdue === null ? '' : String(row.overdue)),
    },
    {
      id: 'state',
      /* Its own heading. This column reused `retain.subject`, so the table had
         two columns headed "What" — seen on the page, not in the code. */
      headingKey: 'retain.state.heading',
      render: (row) => (
        <>
          {tDynamic(`retain.state.${row.state}`, { overdueCount: String(row.overdue ?? 0) })}
          {row.oldestAt === null ? null : (
            <span className="pt-table__sub">{dateInWords(row.oldestAt)}</span>
          )}
        </>
      ),
    },
  ];

  return (
    <AppShell
      title={t('retain.heading')}
      banner={report.phase === 'offline' ? <OfflineStatement /> : null}
    >
      <Statement>{t('retain.lede')}</Statement>
      {/* FIRST, and it is the server's own sentence: this reports, it does not
          delete, and no job is scheduled to. */}
      {data === null ? null : <Statement>{data.statement}</Statement>}

      {report.phase === 'skeleton' || report.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="table" slow={report.slow} label={t('retain.heading')} />
      ) : null}
      {report.phase === 'error' ? <FetchError onRetry={report.reload} /> : null}

      {report.phase !== 'ready' || data === null ? null : (
        <>
          <FactLine emphasis>
            {data.overdueTotal === 0
              ? t('retain.overdueNone')
              : t('retain.overdueTotal', { overdueCount: String(data.overdueTotal) })}
          </FactLine>

          <BackOfficeTable
            caption={t('retain.heading')}
            columns={columns}
            rows={data.lines}
            rowKey={(row) => row.subject}
          />

          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('retain.unmapped.heading')}</h2>
            <Statement>{t('retain.unmapped.lede')}</Statement>
            {data.unmapped.length === 0 ? (
              <FactLine>{t('retain.unmapped.none')}</FactLine>
            ) : (
              <ul>
                {data.unmapped.map((subject) => (
                  <li key={subject} className="pt-row">
                    <FactLine>{subject}</FactLine>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Each subject's own sentence, in the server's words — the table
              says what, these say why it is the shape it is. */}
          <section className="pt-block">
            <ul>
              {data.lines.map((line) => (
                <li key={`${line.subject}-note`} className="pt-row">
                  <FactLine label={line.subject}>{line.note}</FactLine>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </AppShell>
  );
}
